import { useEffect, useMemo, useRef } from 'react';
import { Chart } from 'react-chartjs-2';
import { AutoSizer } from 'react-virtualized';
import { INDICATOR_GROUP } from '@voladeum/client';
import { Chart as ChartJS, registerables } from 'chart.js';
import { CandlestickController, CandlestickElement, OhlcElement } from 'chartjs-chart-financial';
import { candle_default } from 'client/voladeum/candles/defaults';
import { updateSettings } from 'ducks/settings/slices';
import { selectSignal } from 'ducks/signal/selectors';
import { useIndicatorContext } from 'pages/context/IndicatorContext';
import { useStrategyContext } from 'pages/context/StrategyContext';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import {
  getCandlesData,
  getLinesDataset,
  getPriceAxis,
  getTimeseriesAxis,
  getVolumeAxis,
  getVolumeData,
} from 'utils/charts';
import { populateCandle, retrieveCandle } from 'utils/objects';

import { Card } from 'components/cards/Card';

import 'chartjs-adapter-moment';

import { EMPTY_ARRAY } from '../../../../../constants';

import { AdvancedGraphProps } from './types';

import cn from './IndicatorsGraph.module.scss';

ChartJS.register(...registerables, CandlestickController, CandlestickElement, OhlcElement);

export const IndicatorsGraph = ({ candles = EMPTY_ARRAY, visible }: AdvancedGraphProps) => {
  const dispatch = useAppDispatch();
  const candlesRef = useRef<ChartJS>(null);

  const signals = useAppSelector(selectSignal);
  const { indicators: strategy_indicators } = useStrategyContext();
  const { indicators: signal_indicators } = useIndicatorContext();

  useEffect(() => {
    if (candles.length > 0) {
      const { current, previous } = retrieveCandle(candles, candles.length - 1);
      dispatch(updateSettings({ current, previous }));
    } else {
      dispatch(
        updateSettings({
          current: populateCandle(candle_default),
          previous: populateCandle(candle_default),
        })
      );
    }
  }, [candles, candles.length, dispatch]);

  const { min, max } = useMemo(() => {
    const signal = signals['close'] || [[]];
    const values = signal.flatMap((arr) => arr.filter((v): v is number => v !== null));

    if (values.length === 0) return { min: 0, max: 0 };

    const min = Math.min(...values);
    const max = Math.max(...values);
    return { min, max };
  }, [signals]);

  const price_candle = getCandlesData({ data: candles });
  const price_volume = getVolumeData({ data: candles });

  const lines = useMemo(() => {
    const combined_indicators = [...strategy_indicators, ...signal_indicators];

    return combined_indicators
      .filter((indicator) => indicator.indicator_group_id === INDICATOR_GROUP.PRICE)
      .flatMap((indicator) => getLinesDataset(candles, signals, indicator));
  }, [candles, signals, strategy_indicators, signal_indicators]);

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      x: getTimeseriesAxis({ min: visible.start, max: visible.end, display: false }),
      line: getPriceAxis({ scale: max - min, buffer: 0.2 }),
      volume: getVolumeAxis({ multiplier: 3 }),
    },
    layout: { padding: { top: 0, right: 0, bottom: 0, left: 0 } },
    plugins: {
      legend: { display: false },
      crosshairs: { enabled: true },
      price: { enabled: false },
      tooltip: {
        enabled: false,
        external: (context: any) => {
          const tooltip = context.tooltip;

          if (!tooltip.dataPoints?.length) return;

          if (tooltip.opacity === 0) {
            const { current, previous } = retrieveCandle(candles, candles.length - 1);
            dispatch(updateSettings({ current, previous }));
            return;
          }

          const data = tooltip.dataPoints[0];
          const index = data.dataIndex;
          const { current, previous } = retrieveCandle(candles, index);
          dispatch(updateSettings({ current, previous }));
        },
      },
    },
  };

  const candlesticks = { datasets: [price_candle, price_volume, ...lines] };

  return (
    <Card minHeight="250px" direction="column">
      <div className={cn.flex}>
        <AutoSizer>
          {({ height, width }: { height: number; width: number }) => (
            <div className={cn.graph} style={{ height, width }}>
              <Chart
                ref={candlesRef}
                key={`candlestick-${height}-${width}`}
                type="candlestick"
                data={candlesticks}
                options={options}
                width={width}
                height={height}
              />
            </div>
          )}
        </AutoSizer>
      </div>
    </Card>
  );
};
